export const toCurrency = (
  num = 0,
  isoCode = "EUR",
  options: Intl.NumberFormatOptions = {}
) =>
  new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: "currency",
    currency: isoCode,
    ...options,
  }).format(num);

export const toCurrencyWholeNumber = (
  num: number = 0,
  isoCode: string = "EUR"
) => {
  return toCurrency(num, isoCode, {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const toFirstTwoSignificantDecimal = (num: number): string | number => {
  if (Number.isInteger(num)) return num;

  const absNum = Math.abs(num);
  let precision: number;
  let minFractionDigits = 0;

  if (absNum >= 1) {
    // For numbers >= 1, always keep exactly 2 decimals
    precision = 2;
    minFractionDigits = 2;
  } else {
    const decimalSource = absNum.toString().includes("e")
      ? absNum.toFixed(20)
      : absNum.toString();
    const [, decimalPart = ""] = decimalSource.split(".");
    const firstNonZeroIndex = decimalPart.search(/[1-9]/);
    precision =
      firstNonZeroIndex === -1 ? 2 : Math.max(firstNonZeroIndex + 2, 2);
  }

  const multiplier = Math.pow(10, precision);
  const rounded = Math.round(num * multiplier) / multiplier;

  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: precision,
  }).format(rounded);
};

export const toRoundedNumber = (num: number, fractionDigits: number = 2) => {
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: Math.min(2, fractionDigits),
    maximumFractionDigits: fractionDigits,
  }).format(num);
};

type SignDisplayType = "auto" | "never" | "always" | "exceptZero";

export const toPercent = (
  num = 0,
  signDisplay: SignDisplayType = "auto",
  minimumFractionDigits: number = 2,
  maximumFractionDigits: number = 2
) => {
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits,
    maximumFractionDigits,
    style: "percent",
    // @ts-ignore
    signDisplay,
  })
    .format(num)
    .replace(" ", "");
};

export const toRoundedNumberOfLots = (
  num: number,
  maximumFractionDigits = 2
) => {
  return `${new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: Math.min(2, maximumFractionDigits),
    maximumFractionDigits: maximumFractionDigits,
  }).format(num)} St.`;
};

export const toShort = (num: number) =>
  new Intl.NumberFormat("de-DE", {
    notation: "compact",
    compactDisplay: "short",
  })
    .format(num)
    .replace(" ", "");

export const toAmericanShort = (
  num: number,
  minimumFractionDigits = 0,
  maximumFractionDigits = 0
) => {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(num);
};

export const toCustomShort = (num: number) => {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
  })
    .format(num)
    .replace(/[.,KMBT]/g, (x) => {
      switch (x) {
        case ",":
          return ".";
        case ".":
          return ",";
        case "K":
          return "T";
        case "M":
          return "Mio";
        case "B":
          return "Mrd";
        case "T":
          return "Bio";
        default:
          return x;
      }
    });
};

export const toCurrencyCustomShort = (num: number, currency: string) => {
  const abs = Math.abs(num);

  if (abs >= 1_000_000) {
    return toCurrency(num, currency, {
      notation: "compact",
      compactDisplay: "short",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  return toCurrency(num, currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const toGermanNumberFormat = (
  num: number,
  options = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }
) => new Intl.NumberFormat("de-DE", options).format(num);

export const toString = (
  val?: number,
  input = "",
  maximumFractionDigits = 6
) => {
  if (val == null || isNaN(val)) {
    const [integerPart, fractionPart = ""] = input.split(",");
    if (!fractionPart) return input;
    const trimmedFraction = fractionPart.slice(0, maximumFractionDigits);
    return `${integerPart},${trimmedFraction}`;
  }

  const numberFormatter = new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits,
    useGrouping: false,
  });

  const formatted = numberFormatter.format(val);
  const len = formatted.length;
  let substring = input.substring(len);

  if (substring.startsWith(",")) {
    const endIndex = 1 + maximumFractionDigits;
    substring = "," + substring.slice(1, endIndex);
  }

  return substring.startsWith(",") ? formatted + substring : formatted;
};

export const parseOrUndefined = (value: string | null) => {
  try {
    if (value === "" || value === null) return undefined;
    if (value.endsWith(",") || value.endsWith(".")) return undefined;
    // do not use replaceAll because node does not implement it and it will fail in jest
    const usFormat = value.replace(/,/g, ".");
    const parsedValue = parseFloat(usFormat);
    if (Number.isNaN(parsedValue)) return undefined;

    return parsedValue;
  } catch (err) {
    return undefined;
  }
};

export const kFormatter = (val: number | string) => {
  const num = Number(val);
  if (Math.abs(num) > 999) {
    return Math.sign(num) * Number((Math.abs(num) / 1000).toFixed()) + "K";
  }
  return Math.sign(num) * Math.abs(num);
};
